import React, { useRef, useState, useMemo, useEffect } from 'react';
import Form from '../../molecules/Form/Form';
import Field from '../Field';
import Button from '../../molecules/Button';
import Toast from '../../molecules/Toast';
import CardForm from './CardForm';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import countryList from 'react-select-country-list';
import Select from 'react-select';
import { NavLink } from 'react-router-dom';
import DiamondImg from '../../../assets/images/diamond-img.svg';
import userService from '../../../services/userService';
import Loaders from '../Loaders';
import { useForm } from '../../molecules/Form';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../../Context/authContext';
import { StyledBilling } from '../SignUp/SignUp.styles';
import OnboardingTemplate from '../OnboardingTemplate';
import { getPlanType, getUnitPrice } from '../../../helpers/common';
import { IoArrowBack } from 'react-icons/io5';

const planTypes = [
  {
    label: 'Basic Plan',
    value: 'Basic Plan',
  },
  {
    label: 'Pro Plan',
    value: 'Pro Plan',
  },
  {
    label: 'Enterprise Plan',
    value: 'Enterprise Plan',
  },
];
const Billing = ({ fromPricing = false, selectedPlan = null, onCardSaveSuccess = null }) => {
  const [form] = useForm();
  const childRef = useRef();
  const [loading, setLoading] = useState(false);
  const [stripePromise, setStripePromise] = useState();
  const options = useMemo(() => countryList().getData(), []);
  const userEmail = localStorage.getItem('email');
  const [plan, setPlan] = useState('monthly');
  const [planType, setPlanType] = useState(planTypes[1]);

  const isProduction = process.env.REACT_APP_ENV === 'production-micro';

  const [value, setValue] = useState(() =>
    countryList()
      .getData()
      .find(country => country.label === 'United States'),
  );

  const { getPermissions, onLogout, user } = useContextHook(AuthContext, v => ({
    getPermissions: v.getPermissions,
    onLogout: v.onLogout,
    user: v.user,
  }));

  const changeHandler = value => {
    setValue(value);
  };

  const handleSubmit = async e => {
    try {
      if (childRef) {
        const { response, result, error } = await childRef?.current.processHandler({
          fullName: e.billed_name,
          street_address: e.street_address,
          phone_number: e.phone_number,
          email: userEmail,
          country: value.value,
          countryName: value.label,
          price_id: selectedPlan?.id,
        });
        if (result && result?.error) {
          Toast({ type: 'error', message: result.error.message });
        }
      }
    } catch (error) {
      Toast({ type: 'error', message: error.message || 'Failed to save payment method' });
      setLoading(false);
    }
  };

  useEffect(() => {
    userService
      .getStripeConfig()
      .then(async result => {
        const { publishableKey } = result;
        setStripePromise(loadStripe(publishableKey));
      })
      .catch(e => Toast({ type: 'warning', message: e?.message || 'Payment key not found' }));
  }, []);

  useEffect(() => {
    if (fromPricing && user?.data) {
      const userData = user.data;
      const emailValue = userData.email || userEmail || '';

      form.setFieldsValue({
        billed_name: userData.fullName || userData.name || '',
        // street_address: userData.street_address || '',
        // phone_number: userData.phone_number || '',
        email: emailValue,
      });

      if (userData.country) {
        const userCountry = options.find(c => c.value === userData.country || c.label === userData.country);
        if (userCountry) {
          setValue(userCountry);
        }
      }
    } else if (!fromPricing && userEmail) {
      form.setFieldsValue({
        email: userEmail,
      });
    }
  }, [fromPricing, user, form, options, userEmail]);

  useEffect(() => {
    const handleNotification = async event => {
      const data = event.detail;
      if (data.actionType === 'subscription_updated' && data.title === 'Invoice Paid') {
        await getPermissions(true);
        onCardSaveSuccess(true)
      }
    };

    window.addEventListener('subscription-notification', handleNotification);
    return () => {
      window.removeEventListener('subscription-notification', handleNotification);
    };
  }, []);

  return (
    // <OnboardingTemplate>
    <StyledBilling>
      {!fromPricing && (

        <div className="heading-text">
          <h1>Get your Membership</h1>
          <p>Get all access and an extra 20% off when you subscribe annually</p>
        </div>
      )}

      <Form form={form} onSubmit={handleSubmit}>
        <div className="two-col">
          <Form.Item
            label="Billed Name"
            type="text"
            name="billed_name"
            rounded
            placeholder="Enter your full name"
            rules={[
              {
                required: true,
                message: 'Billed name is required',
              },
            ]}>
            <Field />
          </Form.Item>
          <Form.Item
            label="Street Address"
            type="text"
            name="street_address"
            rounded
            placeholder="Enter your street address"
            rules={[
              {
                required: true,
                message: 'Street address is required',
              },
            ]}>
            <Field />
          </Form.Item>
        </div>
        <div className="two-col">
          <Form.Item
            label="Phone Number"
            type="tel"
            name="phone_number"
            rounded
            placeholder="Enter your phone number"
            rules={[
              {
                required: true,
                message: 'Phone number is required',
              },
              {
                validator: (_, value) => {
                  if (!value || isValidPhoneNumber(value)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Please enter a valid phone number'));
                },
              },
            ]}>
            <Field />
          </Form.Item>
          <Form.Item type="email" label="Email" name="email" rounded disabled>
            <Field />
          </Form.Item>
        </div>
        <div className="form-hook-country-field">
          <span className="label">Country</span>
          <Select
            classNamePrefix="react-select"
            options={options}
            value={value}
            onChange={changeHandler}
            formatOptionLabel={country => (
              <div className="flag-holder">
                <img src={`https://flagcdn.com/w40/${country.value.toLowerCase()}.png`} alt={country.label} />
                <span>{country.label}</span>
              </div>
            )}
          />
        </div>
        {stripePromise ? (
          <Elements stripe={stripePromise}>
            <CardForm ref={childRef} setLoading={setLoading} />
          </Elements>
        ) : (
          <Loaders viewLoader />
        )}

        {!fromPricing && (
          <div className="plan-holder">
            <div className="plas-selection">
              <div className="plan-btns">
                <button type="button" className={plan === 'monthly' && 'active'} onClick={() => setPlan('monthly')}>
                  Monthly
                </button>
                <button type="button" className={plan === 'yearly' && 'active'} onClick={() => setPlan('yearly')}>
                  Yearly
                </button>
              </div>
              <div className="plans-select">
                <Select
                  classNamePrefix="react-select"
                  options={planTypes}
                  value={planType}
                  onChange={e => setPlanType(e)}
                />
              </div>
            </div>
            <div className="head">
              <div className="heading-holder">
                <figure className="img-holder">
                  <img src={DiamondImg} alt="diamond-img" width={20} height={20} />
                </figure>
                <span className="heading">{planType.value}</span>
              </div>

              <div className="price-holder">
                <span className="price">$0</span>
                <div>
                  <span className="text bold">For 7 Days</span>
                  <span className="text light">
                    ${getUnitPrice({ plan, planType: planType.value })}/{plan === 'monthly' ? 'month' : 'Year'} after
                    trial
                  </span>
                </div>
              </div>
            </div>
            <span className="continue-text">
              By Continuing, you agree to our <NavLink to="#">Terms and Conditions</NavLink>.
            </span>
          </div>
        )}
        <Button type="submit" loader={loading} disabled={loading}>
          Continue
        </Button>
      </Form>
      {!fromPricing && (
        <div className="back-btn">
          <Button variant="outline" onClick={onLogout} width="150px">
            <IoArrowBack />
            Go Back
          </Button>
        </div>
      )}
    </StyledBilling>
    // </OnboardingTemplate>
  );
};

export default Billing;
